import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Network, Users, Building2, Home, MapPin, Crown, Building, GraduationCap, Users2, UserCheck, ShieldCheck } from 'lucide-react';
import { statsApi } from '../lib/api';
import { formatNumber } from '../components/shared/UI';

const nodeStyles = {
  root: 'bg-gradient-to-br from-orange-500 to-orange-600 text-white',
  l1: 'bg-orange-100 text-orange-700 border-orange-200',
  l2: 'bg-amber-50 text-amber-700 border-amber-200',
  l3: 'bg-orange-50 text-orange-600 border-orange-100',
};

const Node = ({ label, count, sub, style = 'l1', icon: Icon, to }) => {
  const body = (
    <div className={`px-5 py-3 rounded-2xl border-2 ${nodeStyles[style]} shadow-sm text-center min-w-[240px] ${to ? 'hover:shadow-md hover:brightness-95 transition' : ''}`}>
      <div className="flex items-center justify-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4" strokeWidth={2.4} />}
        <p className="text-xs font-extrabold tracking-wide uppercase">{label}</p>
      </div>
      {count !== undefined && count !== null && <p className="text-2xl font-extrabold">{count}</p>}
      {sub && <p className="text-[10px] font-semibold mt-0.5 opacity-75">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const StrukturJaringan = () => {
  const [summary, setSummary] = useState(null);
  const [kecList, setKecList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([statsApi.summary(), statsApi.kecamatanDetail()])
      .then(([s, k]) => { setSummary(s.data); setKecList(k.data); })
      .finally(() => setLoading(false));
  }, []);

  const totalRelawanUnik = kecList.reduce((a, b) => a + (b.total_unik || 0), 0);
  const kecamatanAktif = kecList.filter(k => (k.total_unik || 0) > 0).length;

  const statCards = [
    { l: 'Kecamatan', v: summary?.kecamatan ?? '-', sub: `${kecamatanAktif} aktif ada data`, icon: Building2, to: '/kecamatan' },
    { l: 'Desa / Kelurahan', v: summary?.desa ?? '-', icon: Home, to: '/desa' },
    { l: 'RW Tercover', v: summary?.rw?.value ?? '-', sub: `dari ~${formatNumber(summary?.rw?.total ?? 0)} RW`, icon: MapPin, to: '/rw' },
    { l: 'Total Relawan (unik)', v: formatNumber(totalRelawanUnik), icon: Users, to: '/partisipan' },
  ];

  const jabatanCards = [
    { l: 'DPC Lengkap', v: summary?.pengurus_dpc ?? 0, icon: Crown, to: '/pengurus-dpc?lengkap=true' },
    { l: 'DPRa Lengkap', v: summary?.pengurus_dpra ?? 0, icon: Building, to: '/pengurus-dpra?lengkap=true' },
    { l: 'Penanggung Jawab RW (PJ RW)', v: summary?.pj_rw ?? 0, icon: MapPin, to: '/partisipan?peran=pj_rw' },
    { l: 'Pelopor', v: summary?.pelopor_peran ?? 0, icon: UserCheck, to: '/partisipan?peran=pelopor' },
    { l: 'Non Pelopor', v: summary?.non_pelopor_peran ?? 0, icon: UserCheck, to: '/partisipan?peran=non_pelopor' },
    { l: 'Anggota RKI', v: summary?.rki ?? 0, icon: Users2, to: '/rki' },
    { l: 'Kader Pelopor Aktif', v: summary?.kader?.value ?? 0, icon: Users, to: '/partisipan?peran=pelopor,non_pelopor' },
    { l: 'Saksi TPS', v: summary?.saksi?.value ?? 0, icon: ShieldCheck, to: '/partisipan?peran=saksi' },
    { l: 'Relawan', v: summary?.simpatisan?.value ?? 0, icon: Users, to: '/partisipan?peran=simpatisan' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map(s => (
          <Link key={s.l} to={s.to} className="bg-white rounded-2xl p-5 card-shadow card-hover block hover:border hover:border-orange-200">
            <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-3">
              <s.icon className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-sm font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{loading ? '...' : s.v}</h3>
            {s.sub && <p className="text-[10px] text-gray-400 font-semibold mt-1">{s.sub}</p>}
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-8 card-shadow">
        <h3 className="text-xl font-extrabold mb-2">Hierarki Struktur Organisasi</h3>
        <p className="text-sm text-gray-500 font-medium mb-8">DPD → DPC → DPRa → Penanggung Jawab RW — data real-time dari sistem. Klik kotak untuk lihat datanya.</p>

        <div className="flex flex-col items-center gap-4">
          <Node label="DPD KAB. SUKABUMI" style="root" icon={Network} />
          <div className="w-px h-6 bg-orange-300"></div>
          <Node label="DPC" count={loading ? '...' : formatNumber(summary?.pengurus_dpc ?? 0)} icon={Crown} to="/pengurus-dpc?lengkap=true"
            sub="Lengkap: Ketua, Sekretaris, Bendahara, Kaderisasi, Bidang Perempuan" />
          <div className="w-px h-6 bg-orange-300"></div>
          <Node label="DPRa" count={loading ? '...' : formatNumber(summary?.pengurus_dpra ?? 0)} style="l2" icon={Building} to="/pengurus-dpra?lengkap=true"
            sub="Lengkap: minimal Ketua DPRa" />
          <div className="w-px h-6 bg-orange-300"></div>
          <Node label="Penanggung Jawab RW (PJ RW)" count={loading ? '...' : formatNumber(summary?.pj_rw ?? 0)} style="l3" icon={MapPin} to="/partisipan?peran=pj_rw" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 pt-8 border-t border-gray-100">
          {jabatanCards.map(s => (
            <Link key={s.l} to={s.to} className="flex items-center justify-between p-3 rounded-xl bg-orange-50/50 border border-orange-100 hover:border-orange-300">
              <span className="text-sm font-semibold text-gray-600 flex items-center gap-2"><s.icon className="w-4 h-4 text-orange-500" /> {s.l}</span>
              <span className="text-lg font-extrabold text-orange-600">{loading ? '...' : formatNumber(s.v)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StrukturJaringan;
